import { Link } from 'react-router-dom'
import { ArrowRight, Image as ImageIcon } from 'lucide-react'
import { GradientHoverCard } from '@/components/common/GradientHoverCard'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DiagonalLines } from '@/components/ui/diagonal-lines'
import { SlotImage } from '@/features/designer/components/SlotImage'
import type { SlotKey } from '@/features/designer/slotRegistry'

interface Initiative {
  title: string
  description: string
  to: string
  label: string
  slotKey: SlotKey
  accent: string
}

const initiatives: Initiative[] = [
  {
    title: 'Films',
    description:
      'Our flagship 24-hour dance film series. Five seasons of bold choreography created under one thrilling constraint.',
    to: '/seasons',
    label: 'Watch all seasons',
    slotKey: 'homepage-initiative-films',
    accent: 'text-brand-blue',
  },
  {
    title: 'ODDFEST',
    description:
      'An annual festival showcasing twelve dance filmmakers from around the world, with screenings, panels, and awards.',
    to: '/festivals/oddfest',
    label: 'Learn about ODDFEST',
    slotKey: 'homepage-initiative-oddfest',
    accent: 'text-brand-purple',
  },
  {
    title: 'S.H.I.F.T.',
    description:
      'A live performance series spotlighting fresh choreographic voices on stage, with no application or audition fees, ever.',
    to: '/festivals/shift',
    label: 'Explore S.H.I.F.T.',
    slotKey: 'homepage-initiative-shift',
    accent: 'text-brand-yellow-dark',
  },
]

export function InitiativesSection() {
  return (
    <section className="relative overflow-hidden border-b bg-card py-24 dark:bg-background">
      <DiagonalLines
        className="absolute inset-0 text-brand-orange dark:text-white"
        opacity={0.35}
        strokeWidth={4}
      />
      <div className="container relative mx-auto px-4">
        <Card className="border-0 p-8 shadow-[0_0_60px_-10px_rgba(0,0,0,0.5)] md:p-12 dark:shadow-[0_0_80px_-10px_rgba(0,0,0,0.9)]">
        <div className="mb-12 max-w-2xl">
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-orange">
            Our Initiatives
          </p>
          <h2 className="mb-4 text-4xl font-bold tracking-tight">
            Three platforms. One mission.
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            We champion emerging and mid-career artists through film, performance, and opportunity.
            We believe art should be accessible. We never charge application or audition fees.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {initiatives.map(({ title, description, to, label, slotKey, accent }) => (
            <GradientHoverCard key={title} className="rounded-xl h-full">
              <div className="group relative m-[2px] flex h-[calc(100%-4px)] flex-col overflow-hidden rounded-[10px] bg-background [transform:translateZ(0)] [backface-visibility:hidden]">
                {/* Stretched link — whole card is clickable */}
                <Link
                  to={to}
                  aria-label={label}
                  className="absolute inset-0 z-10"
                />
                <SlotImage
                  slotKey={slotKey}
                  alt={title}
                  className="aspect-video w-full bg-muted"
                  fallback={
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground/50">
                      <ImageIcon className="h-10 w-10" strokeWidth={1.25} />
                    </div>
                  }
                />

                {/* Content — pulled up 1px so its bg covers any sub-pixel seam during hover scale */}
                <div className="relative -mt-[2px] flex flex-1 flex-col gap-4 bg-background p-6">
                  <h3 className={`text-2xl font-bold tracking-tight ${accent}`}>{title}</h3>
                  <p className="flex-1 text-sm text-muted-foreground leading-relaxed">{description}</p>
                  <Button asChild className="relative z-20 self-start">
                    <Link to={to} tabIndex={-1} aria-hidden>
                      {label}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </GradientHoverCard>
          ))}
        </div>
        </Card>
      </div>
    </section>
  )
}
